import * as React from "react";
import { Box, LinearProgress, Stack, TextField } from "@mui/material";

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}
function snap(n, step) {
  if (!step) return n;
  const s = Math.max(step, 0.000001);
  return Math.round(n / s) * s;
}

export default function ProgressWithDraggableDot({
  value,
  lastValue,
  onChange,
  disabled,
  min = 0.0,
  max = 100.0,
  step = 1,
  draggable = true,
  height = 6,
  dotSize = 15,
  barWidth = 0.94,
}) {
  const pct = ((value - min) / (max - min)) * 100;
  const wrapRef = React.useRef(null);
  const draggingRef = React.useRef(false);
  const filled = "#c7d6c7";
  const current = (t) => t.palette["success"].main;
  const empty = "#616161";

  const updateFromClientX = React.useCallback(
    (clientX, commit = false) => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      const rawVal = min + ratio * (max - min);
      const snapped = snap(rawVal, step);
      const next = clamp(snapped, min, max);
      onChange(next - lastValue);
    },
    [min, max, step, onChange, lastValue],
  );

  const onPointerDown = (e) => {
    if (disabled || !draggable) return;
    e.preventDefault();
    const target = wrapRef.current;
    if (!target) return;
    target.setPointerCapture?.(e.pointerId);
    draggingRef.current = true;
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (disabled || !draggingRef.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    if (disabled || !draggingRef.current) return;
    draggingRef.current = false;
    updateFromClientX(e.clientX, true);
  };

  const onKeyDown = (e) => {
    if (disabled || !draggable) return;
    let next = value;
    const big = (max - min) / 10 || step || 1;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown":
        next = clamp(snap(value - step, step), min, max);
        break;
      case "ArrowRight":
      case "ArrowUp":
        next = clamp(snap(value + step, step), min, max);
        break;
      case "Home":
        next = min;
        break;
      case "End":
        next = max;
        break;
      case "PageDown":
        next = clamp(snap(value - big, step), min, max);
        break;
      case "PageUp":
        next = clamp(snap(value + big, step), min, max);
        break;
      default:
        return;
    }
    e.preventDefault();
    onChange?.(next - lastValue);
  };

  return (
    <Box>
      <Box
        ref={wrapRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        sx={{
          position: "relative",
          width: "90%",
          left: "5%",
          height,
          cursor: draggable ? "pointer" : "default",
          overflow: "visible",
        }}
      >
        <LinearProgress
          variant="determinate"
          value={((clamp(value, min, max) - min) / (max - min)) * 100}
          sx={{
            height,
            width: `${barWidth * 100}%`,
            borderRadius: height / 2,
            "& .MuiLinearProgress-bar": {
              borderRadius: height / 2,
              background: current,
            },
            backgroundColor: empty,
            zIndex: (t) => t.zIndex.tooltip - 2,
          }}
        />
        {/* Overlay bar (special color up to threshold) */}
        <LinearProgress
          variant="determinate"
          value={100}
          sx={{
            width: `${barWidth * lastValue}%`,
            height,
            borderRadius: height / 2,
            "& .MuiLinearProgress-bar": {
              borderRadius: height / 2,
              background: filled,
            },
            position: "relative",
            left: 0,
            top: -height,
            zIndex: (t) => t.zIndex.tooltip - 1,
          }}
        />

        {/* Dot (focusable slider handle) */}
        <Box
          role="slider"
          tabIndex={draggable ? 0 : -1}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={Math.floor(value)}
          onKeyDown={onKeyDown}
          sx={(t) => ({
            position: "absolute",
            top: "50%",
            left: `${barWidth * pct}%`,
            transform: "translate(-50%, -50%)",
            width: dotSize,
            height: dotSize,
            borderRadius: "50%",
            bgcolor: current,
            boxShadow: `0 0 0 1px ${t.palette.background.paper}`,
            transition: "left 0ms linear",
            pointerEvents: !disabled && draggable ? "auto" : "none",
            zIndex: (t) => t.zIndex.tooltip,
          })}
        />

        {/* Optional % label above the dot */}
        <Box
          sx={{
            position: "absolute",
            left: `${barWidth * pct}%`,
            transform: "translateX(-50%)",
            bottom: "100%", // put it above the bar
            mb: 0.75, // gap above bar
            p: 0.25,
            px: 1.2,
            fontSize: 14,
            lineHeight: 1,
            color: "text.primary",
            whiteSpace: "nowrap",
            transition: "left 0ms linear",
            background: (t) =>
              `linear-gradient(${t.palette.action.hover}, ${t.palette.action.hover}), ${t.palette.background.paper}`,
            WebkitMaskImage:
              "linear-gradient(to right, transparent, #000 6px, #000 calc(100% - 6px), transparent)",
            maskImage:
              "linear-gradient(to right, transparent, #000 6px, #000 calc(100% - 6px), transparent)",

            pointerEvents: "none",
            zIndex: (t) => t.zIndex.tooltip,
          }}
        >
          {Math.floor(value)}%
        </Box>

        {/* Previous Value marker */}
        <Box
          sx={(t) => ({
            position: "absolute",
            top: "50%",
            left: `${lastValue * barWidth}%`,
            transform: "translate(-50%, -50%)",
            width: "3px",
            height: "15px",
            borderRadius: 1,
            bgcolor: filled,
            transition: draggingRef.current ? "none" : "left 120ms linear",
            outline: "none",
            zIndex: (t) => t.zIndex.tooltip - 1,
          })}
        />

        {/* Previous % Label */}
        {/* <Box
        sx={{
          position: "absolute",
          left: `${lastValue}%`,
          transform: "translateX(-50%)",
          bottom: "100%", // put it above the bar
          mb: 1, // gap above bar
          fontSize: 14,
          lineHeight: 1,
          color: "text.primary",
          whiteSpace: "nowrap",
          transition: "left 400ms linear",
        }}
        aria-hidden
      >
        {Math.round(lastValue)}%
      </Box> */}

        <Box
          sx={{
            position: "absolute",
            left: `100%`,
            transform: "translateX(-50%)",
            bottom: "100%", // put it above the bar
            mb: -1.25, // gap above bar
            mr: 0,
            fontSize: 14,
            lineHeight: 1,
            color: "text.primary",
            whiteSpace: "nowrap",
            transition: "left 400ms linear",
          }}
          aria-hidden
        >
          100%
        </Box>

        <Box
          sx={{
            position: "absolute",
            left: `0%`,
            transform: "translateX(-50%)",
            bottom: "100%", // put it above the bar
            mb: -1.25, // gap above bar
            ml: -1.5,
            fontSize: 14,
            lineHeight: 1,
            color: "text.primary",
            whiteSpace: "nowrap",
            transition: "left 400ms linear",
          }}
          aria-hidden
        >
          0%
        </Box>
      </Box>
    </Box>
  );
}
